import {
  SPEC_CREDIT_PURCHASE_MAX_CENTS,
  SPEC_CREDIT_PURCHASE_MIN_CENTS,
  SPEC_MICROS_PER_CENT,
  SPEC_MICROS_PER_CREDIT,
  SPEC_PURCHASED_CREDIT_TERM_MONTHS,
  SPEC_SPEND_LIMIT_MAX_MICROS,
  type SpecBillingSummary,
} from '@parcel/types';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Progress,
  Skeleton,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@parcel/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Info, Settings2 } from 'lucide-react';
import * as React from 'react';
import {
  buySpecCredits,
  formatSpecBillingDate,
  formatSpecMoney,
  getSpecBilling,
  specBillingSaveMessage,
  updateSpecAutoReload,
  updateSpecSpendLimit,
} from '../../lib/spec-billing.js';
import { isSpecSurfaceVisible } from '../../lib/spec-config.js';
import { useSpecLaunchState } from '../../lib/spec-launch.js';
import { formatSpecBalanceUsd } from '../../lib/spec-usage-guard.js';
import { SettingsPanel } from './settings-shell.js';

/**
 * Everything a workspace can do about the credits it OWNS.
 *
 * Deliberately separate from the AI trial or allowance, which the Usage page
 * shows. An allowance renews and expires with the
 * period and cannot be bought; a balance is money that survives the boundary
 * and funds agents, enrichment, lookups, and the chat that overflows the
 * allowance. One number for both would have to answer for both, and every
 * honest answer differs.
 *
 * The three controls here are the ones a customer can actually exercise. The
 * allowance has none, because it is determined by seat count.
 *
 * All three read ONE source, `getSpecBilling().balance`, rather than splitting
 * the figure across the usage summary and the settings: two sources for one
 * card is how a card renders a balance its own auto-reload state disagrees
 * with.
 *
 * Two rules from the design bind every line below:
 *
 *  - The webhook grants, not the redirect. Nothing here raises a displayed
 *    balance off a Checkout response; a purchase Stripe has accepted and the
 *    webhook has not landed says so and keeps showing the last agreed figure.
 *  - Auto-reload is the dangerous primitive. It is off by default, it requires
 *    a monthly limit, and the limit cannot be cleared while it is on. The
 *    dialog states each rule and the route enforces it.
 */

const READ_FAILURE = 'The credit balance is unavailable right now. Try again in a few minutes.';

/** Stated once, wherever launch state has paused a write the route would 409. */
const PAUSED = 'Spec is read-only right now, so credit changes are paused.';

type SpecCreditBalance = SpecBillingSummary['balance'];

/** The same key `usage-settings-page` and `members-settings-page` read on. */
function specBillingQueryKey(workspaceId: string): readonly unknown[] {
  return ['spec-billing', workspaceId];
}

/** Buttons in a dialog's closing row, matching `manage-seats-dialog.tsx`. */
function DialogActions({ children }: { children: React.ReactNode }) {
  return <div className="flex justify-end gap-2">{children}</div>;
}

/**
 * Microcredits from a typed credit amount, or null when the text is not one.
 *
 * Two decimal places, because one credit is $1.00 and the routes take whole
 * cents: 0.01 credits is one cent, and a third decimal is an amount no route
 * here can accept. Rounded to an integer at the end rather than left as
 * `Number(text) * 1_000_000`, which for "0.07" is not an integer in binary
 * floating point and would be rejected as a non-integer microcredit amount.
 */
function parseCreditsToMicros(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const micros = Math.round(Number(trimmed) * SPEC_MICROS_PER_CREDIT);
  return Number.isSafeInteger(micros) ? micros : null;
}

/**
 * A microcredit amount back into text a credit field can hold. Empty for zero.
 *
 * Zero and unset are the same thing for the two auto-reload fields: the
 * summary reports an unconfigured reload as `0`, and an empty field with a
 * placeholder is the honest rendering of "not set yet".
 *
 * They are NOT the same thing for a spend limit, which is why that field seeds
 * through `limitFieldText` instead. See its docblock.
 */
function creditsFieldText(micros: number): string {
  if (!Number.isFinite(micros) || micros <= 0) return '';
  return String(micros / SPEC_MICROS_PER_CREDIT);
}

/**
 * The same, for the spend limit, where `0` and `null` are opposite states.
 *
 * `null` is NO limit, which is unbounded. `0` is the strictest limit there is:
 * it blocks all credit consumption for the period. Seeding a stored `0` into
 * an empty field would make reopening the dialog and pressing Save convert the
 * strictest possible limit into no limit at all, with no user intent and no
 * warning, because an empty field sends `null`.
 */
function limitFieldText(micros: number | null): string {
  if (micros == null || !Number.isFinite(micros) || micros < 0) return '';
  return String(micros / SPEC_MICROS_PER_CREDIT);
}

/** Whole cents for a microcredit amount that is already a whole number of cents. */
function centsForMicros(micros: number): number {
  return micros / SPEC_MICROS_PER_CENT;
}

/**
 * When credits bought right now would expire.
 *
 * Computed on the client from a FIXED rule
 * (`SPEC_PURCHASED_CREDIT_TERM_MONTHS`), not from a response: the disclosure
 * has to be on screen before the charge, and the grant that will carry the
 * real date does not exist until the webhook lands.
 */
function purchaseExpiryIso(now: Date): string {
  const expires = new Date(now.getTime());
  expires.setUTCMonth(expires.getUTCMonth() + SPEC_PURCHASED_CREDIT_TERM_MONTHS);
  return expires.toISOString();
}

/** The slice of the balance that expires first, or null when nothing does. */
function soonestExpiring(balance: SpecCreditBalance): { micros: number; expiresAt: string } | null {
  let soonest: { micros: number; expiresAt: string } | null = null;
  for (const slice of balance.expiring) {
    if (slice.micros <= 0) continue;
    if (!soonest || Date.parse(slice.expiresAt) < Date.parse(soonest.expiresAt)) soonest = slice;
  }
  return soonest;
}

/** What the balance is, what it pays for, and when it expires, one click away. */
function BalanceInfo({
  allowanceName,
  canBuy,
  expiring,
}: {
  allowanceName: string;
  canBuy: boolean;
  expiring: string | null;
}) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger
          type="button"
          aria-label="About your credit balance"
          className="rounded-full text-muted-foreground/70 normal-case tracking-normal transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Info className="size-3.5" aria-hidden />
        </TooltipTrigger>
        <TooltipContent
          className="max-w-72 space-y-2 text-xs normal-case tracking-normal"
          data-testid="credits-info"
        >
          <p>
            {canBuy
              ? `Credits pay for chat once your ${allowanceName} runs out, and for agents, lookups, and enrichment past its monthly credits. Your ${allowanceName} is always used first.`
              : 'Credits pay for chat before your free AI usage, and for agents, lookups, and enrichment past its monthly credits.'}
          </p>
          <p data-testid="credits-expiring">
            Credits expire 12 months after purchase.{expiring ? ` ${expiring}` : ''}
          </p>
          <p>Payments are processed securely by Stripe.</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * The Buy credits dialog: an amount in dollars, the expiry disclosed before the charge, then
 * Stripe Checkout. Shared by Billing and Usage so both buy the same way.
 */
export function BuyCreditsDialog({
  workspaceId,
  open,
  onOpenChange,
  writesPaused,
  allowanceName,
}: {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  writesPaused: boolean;
  allowanceName: string;
}) {
  const [amountText, setAmountText] = React.useState('25');
  const [starting, setStarting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Every open starts from the default amount and a clean slate.
  React.useEffect(() => {
    if (!open) return;
    setAmountText('25');
    setError(null);
    setStarting(false);
  }, [open]);

  const amountMicros = parseCreditsToMicros(amountText);
  const amountCents = amountMicros == null ? null : centsForMicros(amountMicros);
  const amountSendable =
    amountCents != null &&
    Number.isSafeInteger(amountCents) &&
    amountCents >= SPEC_CREDIT_PURCHASE_MIN_CENTS &&
    amountCents <= SPEC_CREDIT_PURCHASE_MAX_CENTS;
  const expiresOn = formatSpecBillingDate(purchaseExpiryIso(new Date()));

  /**
   * Hands off to Stripe and grants nothing.
   *
   * `starting` is deliberately never cleared on success: the next statement is
   * a full-page navigation, and re-enabling the button in the frame before it
   * only offers a second Checkout session for the same intent.
   */
  const startCheckout = async () => {
    if (!amountSendable || amountCents == null || starting) return;
    setStarting(true);
    setError(null);
    try {
      const session = await buySpecCredits(workspaceId, { amountCents });
      window.location.assign(session.checkoutUrl);
    } catch (err) {
      setError(specBillingSaveMessage(err, 'Unable to start Checkout right now.'));
      setStarting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Buy credits</DialogTitle>
          <DialogDescription>
            Your {allowanceName} is always used before credits bought here.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="credit-amount">Amount ($)</Label>
            <Input
              id="credit-amount"
              inputMode="numeric"
              placeholder="25"
              value={amountText}
              onChange={(event) => setAmountText(event.target.value)}
              disabled={starting || writesPaused}
            />
            <p className="text-sm text-muted-foreground">
              Credits expire 12 months after purchase.
            </p>
          </div>
          {/*
              The disclosure, BEFORE the charge (design section 4a). Rendered
              only for an amount that can actually be bought, so it never
              promises credits at a price the route would refuse.
            */}
          {amountSendable && amountCents != null && expiresOn ? (
            <p
              data-testid="credit-purchase-disclosure"
              className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground"
            >
              You'll add {formatSpecMoney(amountCents)} in credits, expiring {expiresOn}, 12 months
              from today.
            </p>
          ) : (
            <p className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
              Enter an amount between {formatSpecMoney(SPEC_CREDIT_PURCHASE_MIN_CENTS)} and{' '}
              {formatSpecMoney(SPEC_CREDIT_PURCHASE_MAX_CENTS)}.
            </p>
          )}
          {writesPaused ? <p className="text-sm text-muted-foreground">{PAUSED}</p> : null}
          {error ? (
            <p data-testid="credit-purchase-error" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </div>
        <DialogActions>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            type="button"
            onClick={startCheckout}
            disabled={!amountSendable || starting || writesPaused}
          >
            Continue to payment
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}

function BalanceCard({
  workspaceId,
  balance,
  writesPaused,
  canBuy,
  allowanceName,
  onUpgrade,
}: {
  workspaceId: string;
  balance: SpecCreditBalance;
  writesPaused: boolean;
  canBuy: boolean;
  /** "free AI trial", "free AI usage" or "monthly AI usage", the same names chat and Usage use. */
  allowanceName: string;
  onUpgrade: () => void;
}) {
  const [buyOpen, setBuyOpen] = React.useState(false);
  const expiring = soonestExpiring(balance);
  const expiringOn = expiring ? formatSpecBillingDate(expiring.expiresAt) : null;
  const openBuy = () => setBuyOpen(true);

  return (
    <>
      <Card>
        <CardContent className="flex flex-wrap items-end justify-between gap-4 p-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Total available
                <BalanceInfo
                  allowanceName={allowanceName}
                  canBuy={canBuy}
                  expiring={
                    expiring && expiringOn
                      ? `${formatSpecBalanceUsd(expiring.micros)} expires ${expiringOn}.`
                      : null
                  }
                />
              </p>
              {balance.pendingPurchaseCents != null ? (
                <Badge variant="amber" data-testid="credits-pending-badge">
                  {formatSpecMoney(balance.pendingPurchaseCents)} purchase pending
                </Badge>
              ) : null}
            </div>
            <p className="mt-1 text-2xl font-semibold tabular-nums" data-testid="credits-balance">
              {formatSpecBalanceUsd(balance.remainingMicros)}
            </p>
            <p className="mt-0.5 text-sm text-muted-foreground">Pay-as-you-go balance</p>
          </div>
          {canBuy ? (
            <Button type="button" onClick={openBuy}>
              <CreditCard className="size-4" />
              Buy credits
            </Button>
          ) : (
            <Button type="button" onClick={onUpgrade}>
              Upgrade
            </Button>
          )}
        </CardContent>
      </Card>

      <BuyCreditsDialog
        workspaceId={workspaceId}
        open={buyOpen}
        onOpenChange={setBuyOpen}
        writesPaused={writesPaused}
        allowanceName={allowanceName}
      />
    </>
  );
}

/** The balance card's own box: label, figure, hint, and the button beside them. */
function BalanceCardSkeleton() {
  return (
    <Card>
      <CardContent className="flex flex-wrap items-end justify-between gap-4 p-4">
        <div>
          <Skeleton className="h-4 w-28" />
          <Skeleton className="mt-2 h-7 w-32" />
          <Skeleton className="mt-2 h-4 w-80 max-w-full" />
        </div>
        <Skeleton className="h-10 w-32" />
      </CardContent>
    </Card>
  );
}

/**
 * Auto-reload and the monthly limit, side by side.
 *
 * Paired because they are two halves of one decision: reload is what spends a
 * card without anyone watching, and the limit is the brake on it. Each states
 * its condition in a sentence and keeps its controls behind a deliberate
 * click, because a card that is a form cannot be scanned.
 *
 * Each button names what it will do to the state it is looking at, rather than
 * sharing one generic "Manage" -- two identical buttons over two different
 * controls is how a reader learns to stop reading them.
 */
function ReloadAndLimitCards({
  workspaceId,
  balance,
  writesPaused,
  canBuy,
  allowanceName,
  onBalanceChange,
}: {
  workspaceId: string;
  balance: SpecCreditBalance;
  writesPaused: boolean;
  canBuy: boolean;
  allowanceName: string;
  onBalanceChange: (next: SpecCreditBalance) => void;
}) {
  const [reloadOpen, setReloadOpen] = React.useState(false);
  const [limitOpen, setLimitOpen] = React.useState(false);

  const autoReloadEnabled = balance.autoReload.enabled;
  const limitMicros = balance.spendLimit.limitMicros;
  /**
   * A limit auto-reload may run behind, which is a POSITIVE one.
   *
   * The same predicate the route's enable-gate uses,
   * `(settings?.spendLimitMicros ?? 0) <= 0` (spec-usage.ts). Deliberately not
   * reused for the card copy below: a limit of `0` is not "no limit", it is
   * the strictest limit there is, and describing it as unset states the
   * opposite of what it does.
   */
  const hasReloadableLimit = limitMicros != null && limitMicros > 0;

  // Drafts, seeded from the summary each time a dialog opens rather than kept
  // in sync with it: a dialog that re-seeds itself under a background refetch
  // discards what the manager is halfway through typing.
  const [enabledDraft, setEnabledDraft] = React.useState(autoReloadEnabled);
  const [thresholdText, setThresholdText] = React.useState('');
  const [amountText, setAmountText] = React.useState('');
  const [reloadSaving, setReloadSaving] = React.useState(false);
  const [reloadError, setReloadError] = React.useState<string | null>(null);

  const [limitText, setLimitText] = React.useState('');
  const [limitSaving, setLimitSaving] = React.useState(false);
  const [limitError, setLimitError] = React.useState<string | null>(null);

  const openReload = () => {
    setEnabledDraft(autoReloadEnabled);
    setThresholdText(creditsFieldText(balance.autoReload.thresholdMicros));
    setAmountText(creditsFieldText(balance.autoReload.amountMicros));
    setReloadError(null);
    setReloadOpen(true);
  };

  const openLimit = () => {
    setLimitText(limitFieldText(limitMicros));
    setLimitError(null);
    setLimitOpen(true);
  };

  const thresholdMicros = thresholdText.trim() === '' ? 0 : parseCreditsToMicros(thresholdText);
  const amountMicros = amountText.trim() === '' ? 0 : parseCreditsToMicros(amountText);
  const amountCents = amountMicros == null ? null : centsForMicros(amountMicros);
  /**
   * A reload amount the reload itself could actually charge.
   *
   * Bounded by the same purchase limits a manual buy is, because that is what
   * an auto-reload becomes: a Checkout the customer is not watching. A MIRROR
   * of the route's own bound (`parseSpecAutoReloadBody`, spec-usage.ts), not a
   * client-only rule: the point is to say so in the dialog rather than to send
   * a number that can only come back as a 400.
   */
  const amountChargeable =
    amountCents != null &&
    Number.isSafeInteger(amountCents) &&
    amountCents >= SPEC_CREDIT_PURCHASE_MIN_CENTS &&
    amountCents <= SPEC_CREDIT_PURCHASE_MAX_CENTS;
  const reloadDraftValid = enabledDraft
    ? thresholdMicros != null && amountChargeable && hasReloadableLimit
    : thresholdMicros != null && (amountMicros != null || amountText.trim() === '');
  const canSaveReload = Boolean(reloadDraftValid && !reloadSaving && !writesPaused);

  const limitDraftMicros = limitText.trim() === '' ? null : parseCreditsToMicros(limitText);
  const limitParsed = limitText.trim() === '' || limitDraftMicros != null;
  const limitInRange = limitDraftMicros == null || limitDraftMicros <= SPEC_SPEND_LIMIT_MAX_MICROS;
  /**
   * Taking the brake off while auto-reload is on.
   *
   * Both directions, because both reach a state the enable-gate would have
   * refused: `null` is unbounded, and `0` is a limit auto-reload cannot run
   * behind. The spend-limit route answers 409
   * `spec_auto_reload_spend_limit_required` for either.
   */
  const clearsUnderReload =
    autoReloadEnabled && (limitDraftMicros == null || limitDraftMicros <= 0);
  const canSaveLimit = Boolean(
    limitParsed && limitInRange && !clearsUnderReload && !limitSaving && !writesPaused,
  );

  const saveReload = async () => {
    if (!canSaveReload || thresholdMicros == null) return;
    setReloadSaving(true);
    setReloadError(null);
    try {
      const next = await updateSpecAutoReload(workspaceId, {
        enabled: enabledDraft,
        thresholdMicros,
        amountMicros: amountMicros ?? 0,
      });
      // The RESPONSE, never the draft: a rejected or adjusted write must not
      // leave this card displaying the number the manager hoped for.
      onBalanceChange({
        ...balance,
        autoReload: {
          ...balance.autoReload,
          enabled: next.enabled,
          thresholdMicros: next.thresholdMicros,
          amountMicros: next.amountMicros,
        },
      });
      setReloadOpen(false);
    } catch (err) {
      setReloadError(specBillingSaveMessage(err, 'Unable to save these auto top-up settings.'));
    } finally {
      setReloadSaving(false);
    }
  };

  const saveLimit = async () => {
    if (!canSaveLimit) return;
    setLimitSaving(true);
    setLimitError(null);
    try {
      const next = await updateSpecSpendLimit(workspaceId, { limitMicros: limitDraftMicros });
      onBalanceChange({
        ...balance,
        spendLimit: { ...balance.spendLimit, limitMicros: next.limitMicros },
      });
      setLimitOpen(false);
    } catch (err) {
      setLimitError(specBillingSaveMessage(err, 'Unable to save this monthly limit.'));
    } finally {
      setLimitSaving(false);
    }
  };

  const limitResetsOn = formatSpecBillingDate(balance.spendLimit.resetsAt);
  const declinedOn = formatSpecBillingDate(balance.autoReload.disabledByDeclineAt);
  // Free cannot start a new purchase, including turning auto-reload on. A
  // reload that is already on still has to be reachable: the spend-limit
  // route will not clear the limit until reload is disabled, and that limit
  // still caps the purchased credits this workspace kept.
  const showReload = canBuy || autoReloadEnabled;

  return (
    <>
      <div className={`grid gap-4${showReload ? ' sm:grid-cols-2' : ''}`}>
        {showReload ? (
          <Card>
            <CardHeader className="flex-row items-start justify-between gap-2 space-y-0 p-4 pb-2">
              <CardTitle className="text-base">Auto top-up</CardTitle>
              {/*
              The label names the outcome, not the dialog. "Manage" is a promise
              of settings to adjust, and there are none until this is on -- so
              off it reads "Enable", which is the only thing the dialog can
              actually do from here, and on it reads "Manage", which is then
              true.
            */}
              {canBuy || autoReloadEnabled ? (
                <Button type="button" variant="outline" size="sm" onClick={openReload}>
                  <Settings2 className="size-4" />
                  {autoReloadEnabled ? 'Manage' : 'Enable'}
                </Button>
              ) : null}
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <p className="text-sm text-muted-foreground" data-testid="auto-reload-state">
                {autoReloadEnabled && canBuy ? (
                  <>
                    Auto top-up is <span className="font-medium text-foreground">enabled</span> and
                    will add{' '}
                    <span className="font-medium text-foreground tabular-nums">
                      {formatSpecBalanceUsd(balance.autoReload.amountMicros)}
                    </span>{' '}
                    when your balance drops below{' '}
                    <span className="font-medium text-foreground tabular-nums">
                      {formatSpecBalanceUsd(balance.autoReload.thresholdMicros)}
                    </span>
                    .
                  </>
                ) : autoReloadEnabled ? (
                  <>
                    Auto top-up is <span className="font-medium text-foreground">enabled</span> but
                    does not add credits on the Free plan.
                  </>
                ) : (
                  <>
                    Auto top-up is <span className="font-medium text-foreground">disabled</span>.
                  </>
                )}
              </p>
              {/* A decline turns auto-reload off rather than retrying; this is where the customer learns why. */}
              {declinedOn ? (
                <p data-testid="auto-reload-declined" className="mt-1 text-sm text-destructive">
                  Turned off after a declined payment on {declinedOn}.
                </p>
              ) : null}
            </CardContent>
          </Card>
        ) : null}

        <Card>
          <CardHeader className="flex-row items-start justify-between gap-2 space-y-0 p-4 pb-2">
            <CardTitle className="text-base">Monthly limit</CardTitle>
            {/*
              "Adjust" rather than "Manage": a limit is one number, and this
              dialog only ever moves it. It also stops the two cards reading as
              the same control twice.
            */}
            <Button type="button" variant="outline" size="sm" onClick={openLimit}>
              <Settings2 className="size-4" />
              Adjust
            </Button>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            {/* Three states: no limit, a zero limit that blocks every draw, and a real limit. */}
            {limitMicros == null ? (
              <p className="text-sm text-muted-foreground">No limit</p>
            ) : limitMicros <= 0 ? (
              <div className="flex items-center gap-2">
                <Badge variant="red">Spending paused</Badge>
                <span className="text-sm tabular-nums text-muted-foreground">$0</span>
              </div>
            ) : (
              <div className="space-y-2" data-testid="monthly-limit-meter">
                <Progress
                  value={Math.min(
                    100,
                    (balance.spendLimit.consumedThisPeriodMicros / limitMicros) * 100,
                  )}
                  aria-label="Monthly limit used"
                  className="h-1.5"
                />
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="tabular-nums">
                    {formatSpecBalanceUsd(balance.spendLimit.consumedThisPeriodMicros)}
                    <span className="text-muted-foreground">
                      {' '}
                      / {formatSpecBalanceUsd(limitMicros)}
                    </span>
                  </span>
                  {limitResetsOn ? (
                    <span className="text-xs text-muted-foreground">Resets {limitResetsOn}</span>
                  ) : null}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={reloadOpen} onOpenChange={setReloadOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Auto top-up</DialogTitle>
            <DialogDescription>
              Add credits automatically when your balance runs low, so work does not stop
              mid-conversation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <Label htmlFor="auto-reload-enabled">Enable auto top-up</Label>
              <Switch
                id="auto-reload-enabled"
                checked={enabledDraft}
                onCheckedChange={(checked) => {
                  if (!canBuy && checked) return;
                  setEnabledDraft(checked);
                }}
                disabled={reloadSaving || writesPaused || (!canBuy && !enabledDraft)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="auto-reload-threshold">When your balance drops below ($)</Label>
              <Input
                id="auto-reload-threshold"
                inputMode="numeric"
                placeholder="10"
                value={thresholdText}
                onChange={(event) => setThresholdText(event.target.value)}
                disabled={reloadSaving || writesPaused}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="auto-reload-amount">Add this amount ($)</Label>
              <Input
                id="auto-reload-amount"
                inputMode="numeric"
                placeholder="25"
                value={amountText}
                onChange={(event) => setAmountText(event.target.value)}
                disabled={reloadSaving || writesPaused}
              />
            </div>
            {/*
              The rule, stated wherever it currently bites. It is not advice:
              the route answers 409 spec_auto_reload_spend_limit_required for
              exactly this state, and Save stays disabled until the limit exists.
            */}
            {enabledDraft && !hasReloadableLimit ? (
              <p
                data-testid="auto-reload-needs-limit"
                className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground"
              >
                A monthly limit is required before auto top-up can be turned on. Set one under
                Monthly limit first.
              </p>
            ) : null}
            {enabledDraft && hasReloadableLimit ? (
              <p className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
                Each top-up is charged to the card on file. If one is declined, auto top-up turns
                itself off rather than retrying.
              </p>
            ) : null}
            {writesPaused ? <p className="text-sm text-muted-foreground">{PAUSED}</p> : null}
            {reloadError ? (
              <p data-testid="auto-reload-error" className="text-sm text-destructive">
                {reloadError}
              </p>
            ) : null}
          </div>
          <DialogActions>
            <Button type="button" variant="outline" onClick={() => setReloadOpen(false)}>
              Close
            </Button>
            <Button type="button" onClick={saveReload} disabled={!canSaveReload}>
              Save
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>

      <Dialog open={limitOpen} onOpenChange={setLimitOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Monthly limit</DialogTitle>
            <DialogDescription>
              Cap how much this workspace can spend on credits in a period. Your {allowanceName} is
              not affected: a limit applies only to credits.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="monthly-limit">Monthly limit ($)</Label>
              <Input
                id="monthly-limit"
                inputMode="numeric"
                placeholder="100"
                value={limitText}
                onChange={(event) => setLimitText(event.target.value)}
                disabled={limitSaving || writesPaused}
              />
              <p className="text-sm text-muted-foreground">
                Spec stops when the limit is reached, and resumes when the period resets. Leave this
                empty for no limit.
              </p>
            </div>
            {clearsUnderReload ? (
              <p
                data-testid="limit-blocked-by-reload"
                className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground"
              >
                Auto top-up is on, so this workspace has to keep a limit above zero. Turn auto
                top-up off first.
              </p>
            ) : null}
            {!limitInRange ? (
              <p className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
                A monthly limit can be at most {formatSpecBalanceUsd(SPEC_SPEND_LIMIT_MAX_MICROS)}.
              </p>
            ) : null}
            {writesPaused ? <p className="text-sm text-muted-foreground">{PAUSED}</p> : null}
            {limitError ? (
              <p data-testid="monthly-limit-error" className="text-sm text-destructive">
                {limitError}
              </p>
            ) : null}
          </div>
          <DialogActions>
            <Button type="button" variant="outline" onClick={() => setLimitOpen(false)}>
              Close
            </Button>
            <Button type="button" onClick={saveLimit} disabled={!canSaveLimit}>
              Save
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </>
  );
}

function BillingCreditsSectionContent({
  workspaceId,
  writesPaused,
  onUpgrade,
}: {
  workspaceId: string;
  writesPaused: boolean;
  onUpgrade: () => void;
}) {
  const queryClient = useQueryClient();
  const billingQuery = useQuery({
    queryKey: specBillingQueryKey(workspaceId),
    enabled: Boolean(workspaceId),
    queryFn: () => getSpecBilling(workspaceId),
  });

  /**
   * Write what the server just answered into the shared cache, then re-read.
   *
   * Both settings routes echo only the fields they wrote, so the patch covers
   * exactly those and the refetch covers everything they cannot speak for --
   * whether a declined reload is still on record, what the period has consumed
   * so far. Guessing the rest here is how a card ends up asserting a state the
   * ledger never agreed to.
   */
  const applyBalance = React.useCallback(
    (next: SpecCreditBalance) => {
      queryClient.setQueryData(
        specBillingQueryKey(workspaceId),
        (current: SpecBillingSummary | undefined) =>
          current ? { ...current, balance: next } : current,
      );
      void queryClient.invalidateQueries({ queryKey: specBillingQueryKey(workspaceId) });
    },
    [queryClient, workspaceId],
  );

  const summary = billingQuery.data ?? null;
  const allowanceName =
    summary?.eligibility === 'trial'
      ? 'free AI trial'
      : summary?.eligibility === 'free_grant_only'
        ? 'free AI usage'
        : 'monthly AI usage';

  return (
    <SettingsPanel data-testid="credits-section" className="space-y-4 p-4">
      <h3 className="font-medium">Credits</h3>
      {summary ? (
        <>
          <BalanceCard
            workspaceId={workspaceId}
            balance={summary.balance}
            writesPaused={writesPaused}
            canBuy={summary.eligibility !== 'free_grant_only'}
            allowanceName={allowanceName}
            onUpgrade={onUpgrade}
          />
          <ReloadAndLimitCards
            workspaceId={workspaceId}
            balance={summary.balance}
            writesPaused={writesPaused}
            canBuy={summary.eligibility !== 'free_grant_only'}
            allowanceName={allowanceName}
            onBalanceChange={applyBalance}
          />
        </>
      ) : billingQuery.error ? (
        <p className="text-sm text-muted-foreground">{READ_FAILURE}</p>
      ) : (
        <>
          <BalanceCardSkeleton />
          <ReloadAndLimitCardsSkeleton />
        </>
      )}
    </SettingsPanel>
  );
}

/** The two control cards before the summary that fills them has landed. */
function ReloadAndLimitCardsSkeleton() {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {['auto-reload', 'monthly-limit'].map((control) => (
        <Card key={control}>
          <CardHeader className="flex-row items-start justify-between gap-2 space-y-0 p-4 pb-2">
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-8 w-20" />
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <Skeleton className="h-4 w-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/**
 * The whole section, for the moment before Billing even knows which workspace
 * it is looking at.
 *
 * Exported because that moment belongs to the PAGE, not to this component:
 * `BillingCreditsSection` needs a `workspaceId` it does not have yet, so the
 * page renders this in its place rather than mounting a component that would
 * have to invent one.
 */
export function BillingCreditsSectionSkeleton() {
  return (
    <SettingsPanel className="space-y-4 p-4">
      <div>
        <Skeleton className="h-5 w-20" />
        <Skeleton className="mt-2 h-4 w-96 max-w-full" />
      </div>
      <BalanceCardSkeleton />
      <ReloadAndLimitCardsSkeleton />
    </SettingsPanel>
  );
}

/**
 * The launch guard. Owns one hook and returns null above every hook the body
 * below owns, so a mid-session launch flip mounts or unmounts the whole
 * subtree rather than changing a hook count inside a live instance.
 *
 * Also decides whether the writes below are offered at all: all three credit
 * routes answer 409 `spec_read_only` outside launch state `on`, so a
 * read-only workspace pre-disables its controls rather than letting a manager
 * fill in a form that can only be refused. Same rule as the Spec AI card.
 */
export function BillingCreditsSection({
  workspaceId,
  onUpgrade,
}: {
  workspaceId: string;
  onUpgrade: () => void;
}) {
  const launchState = useSpecLaunchState();
  if (!isSpecSurfaceVisible(launchState)) return null;
  return (
    <BillingCreditsSectionContent
      workspaceId={workspaceId}
      writesPaused={launchState !== 'on'}
      onUpgrade={onUpgrade}
    />
  );
}
